import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import StorefrontLayout from "../../components/storefront/StorefrontLayout";
import { api } from "../../lib/api";
import { fmtINR } from "../../lib/constants";
import { useAuth } from "../../lib/auth";
import { toast } from "sonner";
import { Coins, CheckCircle2, Sparkles, TrendingUp } from "lucide-react";

export default function GoldScheme() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [config, setConfig] = useState(null);
  const [mine, setMine] = useState([]);
  const [amount, setAmount] = useState("");
  const [nominee, setNominee] = useState({ nominee_name: "", nominee_relation: "", nominee_phone: "" });
  const [busy, setBusy] = useState(false);

  const loadMine = () => {
    if (!user) return;
    api.get("/api/gold-scheme/mine").then((r) => setMine(r.data || [])).catch(() => {});
  };
  useEffect(() => {
    api.get("/api/gold-scheme/config").then((r) => { setConfig(r.data); setAmount(String(r.data.min_amount || 1000)); }).catch(() => {});
    loadMine();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const active = mine.find((m) => m.status === "active");
  const gramsPreview = config?.rate_per_gram ? (parseFloat(amount || 0) / config.rate_per_gram) : 0;

  const join = async () => {
    if (!user) { toast.error("Please login first"); nav("/login"); return; }
    if (!(parseFloat(amount) >= (config?.min_amount || 1000))) { toast.error(`Minimum monthly investment is ${fmtINR(config?.min_amount || 1000)}`); return; }
    setBusy(true);
    try {
      await api.post("/api/gold-scheme/join", { monthly_amount: parseFloat(amount), ...nominee });
      toast.success("You've joined the Swayambhu Gold Scheme");
      loadMine();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not join"); }
    finally { setBusy(false); }
  };

  const payMonth = async (enr, amount) => {
    const amt = Math.round(+amount || 0);
    if (amt < (config?.min_amount || 1000)) { toast.error(`Minimum this month is ${fmtINR(config?.min_amount || 1000)}`); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/api/gold-scheme/${enr.id}/pay/create-order`, { amount: amt });
      const finish = async (pid, sig) => {
        await api.post("/api/gold-scheme/pay/verify", {
          enrollment_id: enr.id,
          razorpay_order_id: data.razorpay.order_id,
          razorpay_payment_id: pid,
          razorpay_signature: sig,
          amount: amt,
        });
        toast.success(`Instalment ${data.month_no} paid — gold credited`);
        loadMine();
      };
      if (data.razorpay.mock) {
        await finish(`pay_mock_${Date.now()}`, "mock-sig");
      } else {
        const rzp = new window.Razorpay({
          key: data.razorpay.key_id, amount: data.razorpay.amount, currency: data.razorpay.currency,
          order_id: data.razorpay.order_id, name: "Swayambhu Gold", description: `${config?.name} · Month ${data.month_no}`,
          handler: (resp) => finish(resp.razorpay_payment_id, resp.razorpay_signature),
        });
        rzp.open();
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Payment failed"); }
    finally { setBusy(false); }
  };

  return (
    <StorefrontLayout>
      <div className="bg-gradient-to-b from-maroon to-maroon-dark text-pearl">
        <div className="max-w-5xl mx-auto px-4 lg:px-8 py-14 text-center">
          <div className="inline-flex items-center gap-2 eyebrow text-gold mb-3"><Coins size={16}/> {config?.name || "Swayambhu Gold Scheme"}</div>
          <h1 className="font-display text-4xl lg:text-5xl">Invest any amount monthly, grow your gold</h1>
          <p className="text-pearl/75 mt-4 max-w-2xl mx-auto text-sm">{config?.description}</p>
          {config?.rate_per_gram > 0 && (
            <div className="mt-4 inline-flex items-center gap-2 bg-pearl/10 px-4 py-2 rounded-full text-sm">
              <TrendingUp size={14} className="text-gold"/> Today's {config?.purity} rate: <b>{fmtINR(config.rate_per_gram)}/g</b>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 lg:px-8 py-10">
        {/* How it works */}
        <div className="grid sm:grid-cols-3 gap-4 mb-10">
          {[
            ["Invest monthly", "Pay a flexible amount you choose every month for 12 months — minimum ₹5,000. Different amount every month is fine."],
            ["You get what you pay", "Whatever you pay is converted to gold at that day's rate and credited to your account."],
            ["Free making at the end", "After 12 months, take your accumulated gold and we waive the making charges."],
          ].map(([t, d], i) => (
            <div key={i} className="bg-white border border-line p-5">
              <div className="w-8 h-8 rounded-full bg-maroon text-pearl flex items-center justify-center font-display mb-3">{i + 1}</div>
              <div className="font-medium text-ink">{t}</div>
              <p className="text-xs text-ash mt-1">{d}</p>
            </div>
          ))}
        </div>

        {/* My active scheme, or join */}
        {active ? (
          <SchemeProgress enr={active} onPay={(amt) => payMonth(active, amt)} busy={busy} config={config} />
        ) : (
          <div className="bg-white border-2 border-gold p-6 max-w-lg mx-auto" data-testid="join-card">
            <h2 className="font-display text-2xl text-maroon mb-1">Join the scheme</h2>
            <p className="text-xs text-ash mb-4">Set a starting amount to join. Each month you can pay any amount you like (minimum ₹5,000).</p>
            <label className="block mb-3">
              <span className="text-[10px] text-ash uppercase tracking-widest">Starting monthly amount (₹) — you can change it every month</span>
              <input type="number" min={config?.min_amount || 1000} step="500" value={amount} onChange={(e) => setAmount(e.target.value)}
                className="w-full border border-line px-3 py-2 bg-pearl mt-1" data-testid="scheme-amount"/>
            </label>
            {config?.rate_per_gram > 0 && parseFloat(amount) > 0 && (
              <div className="text-xs text-ash mb-4">
                At today's rate that's about <b className="text-maroon">{gramsPreview.toFixed(3)} g</b> of {config.purity} gold each month,
                roughly <b className="text-maroon">{(gramsPreview * 12).toFixed(3)} g</b> over 12 months.
              </div>
            )}
            <div className="grid sm:grid-cols-3 gap-2 mb-4">
              <input placeholder="Nominee name" className="border border-line px-3 py-2 bg-pearl text-sm" value={nominee.nominee_name} onChange={(e) => setNominee({ ...nominee, nominee_name: e.target.value })}/>
              <input placeholder="Relation" className="border border-line px-3 py-2 bg-pearl text-sm" value={nominee.nominee_relation} onChange={(e) => setNominee({ ...nominee, nominee_relation: e.target.value })}/>
              <input placeholder="Nominee phone" className="border border-line px-3 py-2 bg-pearl text-sm" value={nominee.nominee_phone} onChange={(e) => setNominee({ ...nominee, nominee_phone: e.target.value })}/>
            </div>
            <button onClick={join} disabled={busy} className="btn-maroon w-full justify-center" data-testid="scheme-join">
              {busy ? "…" : (user ? "Join & start saving" : "Login to join")}
            </button>
          </div>
        )}

        {/* Completed schemes */}
        {mine.filter((m) => m.status === "completed").map((m) => (
          <div key={m.id} className="mt-8"><SchemeProgress enr={m} completed /></div>
        ))}
      </div>
    </StorefrontLayout>
  );
}

function SchemeProgress({ enr, onPay, busy, completed, config }) {
  const paid = enr.installments_paid || 0;
  const total = enr.duration_months || 12;
  const pct = Math.round((paid / total) * 100);
  const minAmt = config?.min_amount || 1000;
  const [amt, setAmt] = useState(String(enr.monthly_amount || minAmt));
  const rate = config?.rate_per_gram || 0;
  const grams = rate ? (parseFloat(amt || 0) / rate) : 0;
  return (
    <div className="bg-white border border-line p-6" data-testid={`scheme-progress-${enr.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="font-display text-2xl text-maroon">{enr.scheme_name}</h2>
          <div className="text-xs text-ash">Flexible · min {fmtINR(minAmt)}/month · {enr.purity}</div>
        </div>
        {completed || enr.making_free ? (
          <span className="inline-flex items-center gap-1 bg-green-100 text-green-800 text-xs px-3 py-1.5 rounded-full"><Sparkles size={13}/> Completed · Making charges free</span>
        ) : null}
      </div>

      {/* Pay this month — flexible amount */}
      {!(completed || enr.making_free) && (
        <div className="bg-pearl border border-line p-4 mb-4">
          <div className="text-[10px] text-ash uppercase tracking-widest mb-1">Pay month {paid + 1} — enter any amount (min {fmtINR(minAmt)})</div>
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex items-center gap-2 flex-1 min-w-[160px]">
              <span className="font-display text-xl text-maroon">₹</span>
              <input type="number" min={minAmt} step="500" value={amt} onChange={(e) => setAmt(e.target.value)} className="w-full border border-line px-3 py-2 bg-white" data-testid={`scheme-amt-${enr.id}`}/>
            </div>
            <button onClick={() => onPay(amt)} disabled={busy} className="btn-maroon" data-testid={`scheme-pay-${enr.id}`}>{busy ? "…" : `Pay ${fmtINR(Math.round(+amt || 0))}`}</button>
          </div>
          {rate > 0 && parseFloat(amt) > 0 && (
            <div className="text-xs text-ash mt-2">At today's rate that's about <b className="text-maroon">{grams.toFixed(3)} g</b> credited to your account.</div>
          )}
        </div>
      )}

      {/* Progress bar */}
      <div className="mb-4">
        <div className="flex justify-between text-xs text-ash mb-1"><span>{paid} of {total} instalments</span><span>{pct}%</span></div>
        <div className="h-2 bg-pearl rounded-full overflow-hidden"><div className="h-full bg-gold" style={{ width: `${pct}%` }}/></div>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
        <Stat label="Amount paid" value={fmtINR(enr.total_paid || 0)}/>
        <Stat label="Gold accumulated" value={`${(enr.total_grams || 0).toFixed(3)} g`}/>
        <Stat label="Status" value={enr.status === "completed" ? "Completed" : "Active"}/>
      </div>

      {/* Instalment ledger */}
      {(enr.installments || []).length > 0 && (
        <div className="border border-line overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-pearl border-b border-line"><tr>{["#", "Paid on", "Amount", "Rate/g locked", "Gold (g)"].map((h) => <th key={h} className="text-left p-2.5 eyebrow text-[10px]">{h}</th>)}</tr></thead>
            <tbody>
              {enr.installments.map((it) => (
                <tr key={it.payment_id} className="border-b border-line/60">
                  <td className="p-2.5">{it.month_no}</td>
                  <td className="p-2.5 text-xs">{new Date(it.paid_at).toLocaleDateString()}</td>
                  <td className="p-2.5">{fmtINR(it.amount)}</td>
                  <td className="p-2.5 text-xs">{fmtINR(it.rate_per_gram)}</td>
                  <td className="p-2.5">{(it.grams || 0).toFixed(3)} g</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(completed || enr.making_free) && (
        <div className="mt-4 flex items-center gap-2 text-sm text-green-800 bg-green-50 border border-green-200 p-3">
          <CheckCircle2 size={16}/> You've saved {(enr.total_grams || 0).toFixed(3)} g of gold. Making charges are waived on your chosen piece — visit any showroom to redeem.
        </div>
      )}
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div className="bg-pearl border border-line p-3">
    <div className="eyebrow text-[10px]">{label}</div>
    <div className="font-display text-xl text-maroon">{value}</div>
  </div>
);
